import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomButton from "../CustomButton/CustomButton";
import CustomNewModal from "../CustomNewModal/CustomNewModal";
import { languageArray } from "../LanguageSelector/LanguageSelectorConstant";
import { styles } from "./LanguagePreferencePopupStyles";
import { getItem } from "../../Auth/localStorageService";

const LanguagePreferencePopup = ({ open, onClose, onSelect }) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState("");

  const getBrowserLanguageKey = () => {
    if (typeof navigator === "undefined") return languageArray[0]?.key;
    const lang = navigator.language || "";
    return lang.startsWith("es")
      ? languageArray.find((l) => l.key === "es")?.key
      : languageArray.find((l) => l.key === "en")?.key;
  };

  const selectedLanguageLocale =
    languageArray.find((l) => l.key === selectedLanguage)?.locale ||
    languageArray.find((l) => l.key === getBrowserLanguageKey())?.locale ||
    languageArray[0]?.locale ||
    "en";

  const getInitialLanguage = () => {
    // Prefer saved preference (if any)
    try {
      const saved = getItem("language");
      if (saved) return saved;
    } catch (e) {
      // ignore
    }

    // Else default to browser language
    return getBrowserLanguageKey() || languageArray[0]?.key || "";
  };

  useEffect(() => {
    if (!open) return;
    setSelectedLanguage(getInitialLanguage());
  }, [open]);

  const handleSave = () => {
    if (selectedLanguage) {
      onSelect(selectedLanguage);
    }
  };

  return (
    <CustomNewModal
      openModal={open}
      closeModalHandler={onClose}
      fullWidth
      maxWidth="xs"
      omitCloseButton
    >
      <Box sx={styles.container}>
        <Typography sx={styles.heading}>
          {t("CHOOSE_DEFAULT_LANGUAGE", { lng: selectedLanguageLocale })}
        </Typography>
        <Typography sx={styles.description}>
          {t("LANGUAGE_CHANGE_SUBHEADING", { lng: selectedLanguageLocale })}
        </Typography>

        <Select
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          displayEmpty
          fullWidth
          sx={styles.select}
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography sx={styles.placeholder}>
                  {t("SELECT_LANGUAGE", { lng: selectedLanguageLocale })}
                </Typography>
              );
            }

            const lang = languageArray.find((l) => l.key === value);
            return (
              <Box sx={styles.selectedValue}>
                <Typography
                  component="img"
                  src={lang.flagImage}
                  alt=""
                  sx={styles.flag}
                />
                <Typography sx={styles.selectedLabel}>{lang.label}</Typography>
              </Box>
            );
          }}
        >
          {languageArray.map(({ key, label, flagImage }) => (
            <MenuItem key={key} value={key} sx={styles.menuItem}>
              <Typography
                component="img"
                src={flagImage}
                alt=""
                sx={styles.flag}
              />
              <Typography>{label}</Typography>
            </MenuItem>
          ))}
        </Select>

        <CustomButton
          isDisabled={!selectedLanguage}
          onClick={handleSave}
          classes={styles.saveButton}
        >
          {t("SAVE", { lng: selectedLanguageLocale })}
        </CustomButton>
      </Box>
    </CustomNewModal>
  );
};

LanguagePreferencePopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
};

export default LanguagePreferencePopup;
